<template>
  <div class="forgot-password-view">
    <!-- Intro card -->
    <div class="intro-card mb-5">
      <div class="intro-icon">
        <i class="fas fa-key-skeleton" />
        <div class="icon-fallback">
          <i class="fas fa-key" />
        </div>
      </div>
      <h1 class="title has-text-centered mb-3">
        {{ $t('auth.reset-password') }}
      </h1>
      <div class="lock-animation">
        <i class="fas fa-unlock-alt" />
      </div>
    </div>

    <!-- Success state -->
    <div
      v-if="isSuccess"
      class="success-message"
    >
      <div class="success-container">
        <div class="success-icon">
          <i class="fas fa-envelope" />
        </div>
        <h3 class="success-title">
          {{ $t('auth.email-sent') }}
        </h3>
        <p class="success-text">
          {{ $t('auth.reset-email-sent') }}
        </p>
      </div>

      <div class="has-text-centered mt-5">
        <router-link
          :to="{ name: 'login' }"
          class="back-to-login-link"
        >
          <i class="fas fa-arrow-left mr-2" />
          {{ $t('auth.back-to-login') }}
        </router-link>
      </div>
    </div>

    <!-- Request form -->
    <form
      v-else
      class="reset-form"
      @submit.prevent="submitResetRequest"
    >
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.password-recovery') }}</span>
        <div class="divider-line" />
      </div>

      <p class="instructions mb-4">
        {{ $t('auth.reset-instructions') }}
      </p>

      <div class="indiefy-field">
        <BaseInput
          v-model="email"
          :label="$t('auth.email')"
          :placeholder="$t('auth.email')"
          type="email"
          data-test="email-input"
          :has-error="!!formErrors.email"
          :error-message="formErrors.email"
          left-icon="fas fa-envelope"
          has-floating-label
          required
          autocomplete="email"
          @blur="validateEmail"
        />
      </div>

      <div class="mt-5">
        <BaseButton
          type="submit"
          variant="primary"
          is-fullwidth
          :is-loading="isLoading"
          data-test="reset-password-button"
          is-gradient
          is-3d
        >
          {{ $t('auth.send-reset-link') }}
        </BaseButton>
      </div>

      <div
        v-if="error"
        class="error-container mt-4 p-3"
      >
        <i class="fas fa-exclamation-circle mr-2" />
        {{ error }}
        <div class="support-link mt-2">
          <i class="fas fa-life-ring mr-1" />
          {{ $t('auth.need-help') }}
          <a
            href="https://help.indiefy.net/"
            target="_blank"
            rel="noopener noreferrer"
            class="help-link"
          >
            {{ $t('auth.contact-support') }}
          </a>
        </div>
      </div>

      <div class="secure-info mt-4">
        <div class="secure-badge">
          <i class="fas fa-shield-alt mr-1" />
          {{ $t('auth.secure-process') }}
        </div>
        <p class="secure-text">
          {{ $t('auth.reset-security-note') }}
        </p>
      </div>

      <div class="form-divider mt-5 mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.or') }}</span>
        <div class="divider-line" />
      </div>

      <div class="alternate-options">
        <router-link
          :to="{ name: 'login' }"
          class="alternate-link"
        >
          <i class="fas fa-sign-in-alt mr-1" />
          {{ $t('auth.back-to-login') }}
        </router-link>

        <router-link
          :to="{ name: 'signup' }"
          class="alternate-link"
        >
          <i class="fas fa-user-plus mr-1" />
          {{ $t('auth.create-new-account') }}
        </router-link>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useI18n } from 'vue-i18n';
import authService from '@/services/authService';
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'auth.forgot-title',
  descriptionKey: 'auth.forgot-description',
});

// i18n
const { t } = useI18n();

// Form state
const email = ref('');
const formErrors = ref({
  email: '',
});
const isLoading = ref(false);
const error = ref('');
const isSuccess = ref(false);

// Form validation
const validateEmail = () => {
  if (!email.value) {
    formErrors.value.email = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    formErrors.value.email = t('errors.email-invalid');
  } else {
    formErrors.value.email = '';
  }
};

const validateForm = () => {
  validateEmail();
  return !formErrors.value.email;
};

// Form submission
const submitResetRequest = async () => {
  if (!validateForm()) return;

  isLoading.value = true;
  error.value = '';

  try {
    await authService.getPasswordResetLink({ email: email.value });
    isSuccess.value = true;
  } catch (err) {
    const status = err.response?.status;
    if (status === 404 || status === 400) {
      error.value = t('auth.email-not-found');
    } else {
      error.value = err.response?.data?.message || t('auth.reset-error');
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/abstracts/colors';
@import '@/styles/abstracts/breakpoints';

.forgot-password-view {
  .title {
    font-size: 1.75rem;
    font-weight: 700;
    background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;

    @media screen and (min-width: $tablet) {
      font-size: 2rem;
    }
  }

  // Intro card
  .intro-card {
    padding: 2.5rem 1.5rem;
    position: relative;
    border-radius: 16px;
    text-align: center;
    background: linear-gradient(135deg,
      rgba(var(--color-primary-rgb, 62, 142, 208), 0.05) 0%,
      rgba(var(--color-secondary-rgb, 124, 58, 237), 0.05) 100%
    );
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .body--dark & {
      border-color: rgba(255, 255, 255, 0.05);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.03' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E");
      z-index: 0;
      opacity: 0.5;

      .body--dark & {
        opacity: 0.2;
      }
    }
  }

  .intro-icon {
    width: 70px;
    height: 70px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    position: relative;
    box-shadow: 0 8px 20px rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);
    z-index: 1;

    .icon-fallback {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeInOut 3s infinite;
    }
  }

  .lock-animation {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 1.5rem;
    color: var(--color-primary-lighter, #7cb0dd);
    opacity: 0.5;
    animation: floatUpDown 2s infinite ease-in-out;

    &::before {
      content: '';
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 20px;
      background: linear-gradient(
        to bottom,
        transparent,
        var(--color-primary-lighter, #7cb0dd)
      );
      opacity: 0.3;
    }
  }

  // Form divider
  .form-divider {
    display: flex;
    align-items: center;
    margin: 1.5rem 0;

    .divider-line {
      flex: 1;
      height: 1px;
      background-color: rgba(0, 0, 0, 0.1);

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .divider-text {
      padding: 0 1rem;
      font-size: 0.875rem;
      color: var(--color-text-light, #64748b);

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }

  // Reset form
  .reset-form {
    .instructions {
      font-size: 0.95rem;
      color: var(--color-text-light, #64748b);
      line-height: 1.6;
      text-align: center;

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }

  // Secure information
  .secure-info {
    background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.05);
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid rgba(var(--color-primary-rgb, 62, 142, 208), 0.1);

    .secure-badge {
      display: inline-flex;
      align-items: center;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--color-primary, #{$indiefy-blue});
      background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.1);
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      margin-bottom: 0.5rem;
    }

    .secure-text {
      font-size: 0.85rem;
      color: var(--color-text-light, #64748b);
      margin: 0;

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }

  // Error container
  .error-container {
    background-color: rgba(239, 68, 68, 0.1);
    border-left: 4px solid #ef4444;
    border-radius: 4px;
    color: #ef4444;
    animation: shake 0.5s ease;

    .support-link {
      font-size: 0.85rem;
      color: var(--color-text-light, #64748b);
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.25rem;

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }

      .help-link {
        color: var(--color-primary, #{$indiefy-blue});
        text-decoration: underline;
        font-weight: 500;
        transition: color 0.2s ease;

        &:hover {
          color: var(--color-primary-darker, darken($indiefy-blue, 10%));
        }
      }
    }
  }

  // Alternate options
  .alternate-options {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;

    .alternate-link {
      display: inline-flex;
      align-items: center;
      color: var(--color-primary, #{$indiefy-blue});
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 500;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.05);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  // Success message
  .success-message {
    .success-container {
      padding: 2.5rem 1.5rem;
      background-color: rgba(16, 185, 129, 0.05);
      border: 1px solid rgba(16, 185, 129, 0.1);
      border-radius: 16px;
      text-align: center;
      position: relative;
      overflow: hidden;

      .success-icon {
        width: 60px;
        height: 60px;
        margin: 0 auto 1.5rem;
        background-color: #10b981;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
      }

      .success-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: #10b981;
      }

      .success-text {
        color: var(--color-text, #0f172a);
        font-size: 0.95rem;
        line-height: 1.6;
        max-width: 300px;
        margin: 0 auto;

        .body--dark & {
          color: var(--color-text, #f8fafc);
        }
      }
    }

    .back-to-login-link {
      display: inline-flex;
      align-items: center;
      padding: 0.75rem 1.5rem;
      background-color: var(--color-card-background, #ffffff);
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      color: var(--color-text, #0f172a);
      font-weight: 500;
      text-decoration: none;
      transition: all 0.2s ease;

      .body--dark & {
        background-color: var(--color-card-background, #1e293b);
        border-color: rgba(255, 255, 255, 0.1);
        color: var(--color-text, #f8fafc);
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

        i {
          transform: translateX(-3px);
        }
      }

      &:active {
        transform: translateY(0);
      }

      i {
        transition: transform 0.2s ease;
      }
    }
  }
}

// Animations
@keyframes fadeInOut {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes floatUpDown {
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(-5px);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

@keyframes flyPlane {
  0% {
    transform: translateY(-50%) translateX(0) rotate(0);
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    transform: translateY(-50%) translateX(calc(100vw + 20px)) rotate(5deg);
    opacity: 0;
  }
}
</style>